import { useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { getStatsBookings } from '../../lib/api';
import { formatPct, pctDelta } from '../../lib/format';
import { KpiCard } from '../../components/stats/KpiCard';
import { BarChart } from '../../components/stats/BarChart';
import { BreakdownList } from '../../components/stats/BreakdownList';
import { SectionFrame } from '../../components/stats/SectionFrame';
import { PS, T } from '../../lib/theme';
import type { BookingStats } from '../../lib/types';

export function BookingsSection({ start, end }: { start: string; end: string }) {
  const loader = useCallback(
    (signal: AbortSignal) => getStatsBookings(start, end, signal),
    [start, end],
  );
  const { data, loading, error } = useApi<BookingStats>(loader);

  return (
    <SectionFrame title="Bookings" loading={loading} error={error}>
      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <KpiCard
              label="Confirmed bookings"
              value={data.confirmed_count.toLocaleString('en-US')}
              delta={pctDelta(data.confirmed_count, data.prev_confirmed_count)}
            />
            <KpiCard label="Hours played" value={data.hours_played.toLocaleString('en-US')} />
            <KpiCard label="Cancellation rate" value={formatPct(data.cancellation_rate * 100)} />
          </div>
          <div>
            <div style={{ ...T.label, marginBottom: 6 }}>Confirmed bookings per day</div>
            <BarChart
              data={data.daily.map((d) => ({ label: d.date, value: d.value }))}
              color={PS.tealDeep}
              format={(value) => `${value.toLocaleString('en-US')} ${value === 1 ? 'booking' : 'bookings'}`}
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ ...T.label, marginBottom: 6 }}>By table</div>
              <BreakdownList data={data.by_table} />
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ ...T.label, marginBottom: 6 }}>By source</div>
              <BreakdownList data={data.by_source} />
            </div>
          </div>
        </>
      )}
    </SectionFrame>
  );
}
